import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { addCoupon, editCoupon, getSingleCoupon } from "../../redux/services/couponSlice";
import { ROUTES } from "../../constant";

const discountTypeOptions = ["percentage", "fixed"];
const audienceOptions = ["default", "telecaller", "user"];

const CouponForm = ({ action }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const singleCoupon = useSelector((state) => state.coupon.singleCouponData);
  const queryParams = new URLSearchParams(location.search);
  const couponId = queryParams.get("couponId");
  const [loading, setLoading] = useState(false);


  const initialValues = {
    code: "",
    description: "",
    discountType: "percentage",
    discountValue: 0,
    audience: "default",
    minOrderAmount: 0,
    maxDiscountAmount: 0,
    usageLimitTotal: 0,
    usageLimitPerUser: 0,
    validFrom: "",
    validTill: "",
  };

  const validationSchema = Yup.object({
    code: Yup.string().required("Code is required"),
    description: Yup.string().required("Description is required"),
    discountType: Yup.string().required("Discount type is required"),
    discountValue: Yup.number().min(0, "Must be 0 or more").required("Discount value is required"),
    minOrderAmount: Yup.number().min(0, "Must be 0 or more"),
    maxDiscountAmount: Yup.number().min(0, "Must be 0 or more"),
    usageLimitTotal: Yup.number().min(0, "Must be 0 or more"),
    usageLimitPerUser: Yup.number().min(0, "Must be 0 or more"),
    validFrom: Yup.date().required("Valid From is required"),
    validTill: Yup.date()
      .required("Valid Till is required")
      .min(Yup.ref("validFrom"), "Valid Till must be after Valid From"),
  });

  const onSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      let response;
      if (couponId) {
        response = await dispatch(editCoupon({ ...values, couponId }));
      } else {
        response = await dispatch(addCoupon(values));
      }

      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message, {
          autoClose: 2000,
          pauseOnHover: false,
        });
        navigate(ROUTES.COUPON.BASE);
        resetForm();
      }
    } catch (error) {
      console.error("Error saving coupon:", error);
    } finally {
      setLoading(false);
    }
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const getSingleRecord = useCallback(async () => {
    try {
      await dispatch(getSingleCoupon(couponId));
    } catch (error) {
      console.error("Error fetching coupon:", error);
    }
  }, [dispatch, couponId]);

  useEffect(() => {
    if (couponId) {
      getSingleRecord();
    }
  }, [couponId, getSingleRecord]);

  useEffect(() => {
    if (couponId && singleCoupon) {
      setValues({
        code: singleCoupon?.code ?? "",
        description: singleCoupon?.description ?? "",
        discountType: singleCoupon?.discountType ?? "percentage",
        discountValue: singleCoupon?.discountValue ?? 0,
        audience: singleCoupon?.audience ?? "default",
        minOrderAmount: singleCoupon?.minOrderAmount ?? 0,
        maxDiscountAmount: singleCoupon?.maxDiscountAmount ?? 0,
        usageLimitTotal: singleCoupon?.usageLimitTotal ?? 0,
        usageLimitPerUser: singleCoupon?.usageLimitPerUser ?? 0,
        validFrom: singleCoupon?.validFrom
          ? new Date(singleCoupon.validFrom).toISOString().slice(0, 16)
          : "",
        validTill: singleCoupon?.validTill
          ? new Date(singleCoupon.validTill).toISOString().slice(0, 16)
          : "",
      });
    }
  }, [couponId, singleCoupon, setValues]);

  return (
    <div>
      <div className="bg-background py-4 sm:py-4">
        <div className="px-4 md:py-10 py-5 bg-card shadow rounded-3xl sm:p-10">
          <p className="md:text-3xl text-xl text-center font-medium uppercase text-primary">
            {action} Coupon
          </p>
          <form className="mt-6" onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-2 grid-cols-1 gap-5">

              {/* Code */}
              <div>
                <label className="text-sm font-medium text-foreground">Code</label>
                <input
                  type="text"
                  name="code"
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card"
                  value={values.code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Coupon Code"
                />
                {touched.code && errors.code && (
                  <div className="text-red-500 text-sm">{errors.code}</div>
                )}
              </div>

              {/* Discount Type */}
              <div>
                <label className="text-sm font-medium text-foreground">Discount Type</label>
                <select
                  name="discountType"
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card"
                  value={values.discountType}
                  onChange={handleChange}
                >
                  {discountTypeOptions.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Discount Value */}
              <div>
                <label className="text-sm font-medium text-foreground">Discount Value</label>
                <input
                  type="number"
                  name="discountValue"
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card"
                  value={values.discountValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g. 50"
                />
                {touched.discountValue && errors.discountValue && (
                  <div className="text-red-500 text-sm">{errors.discountValue}</div>
                )}
              </div>

              {/* Audience */}
              <div>
                <label className="text-sm font-medium text-foreground">Audience</label>
                <select
                  name="audience"
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card"
                  value={values.audience}
                  onChange={handleChange}
                >
                  {audienceOptions.map((aud) => (
                    <option key={aud} value={aud}>{aud}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-foreground">Description</label>
                <textarea
                  name="description"
                  rows={3}
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Description"
                />
                {touched.description && errors.description && (
                  <div className="text-red-500 text-sm">{errors.description}</div>
                )}
              </div>

              {/* Numeric Fields */}
              {[
                { name: "minOrderAmount", label: "Min Order Amount" },
                { name: "maxDiscountAmount", label: "Max Discount Amount" },
                { name: "usageLimitTotal", label: "Usage Limit (Total)" },
                { name: "usageLimitPerUser", label: "Usage Limit (Per User)" },
              ].map((field) => (
                <div key={field.name}>
                  <label className="text-sm font-medium text-foreground">{field.label}</label>
                  <input
                    type="number"
                    name={field.name}
                    className="w-full px-4 py-2 border border-border rounded-xl bg-card"
                    value={values[field.name]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={field.label}
                  />
                  {touched[field.name] && errors[field.name] && (
                    <div className="text-red-500 text-sm">{errors[field.name]}</div>
                  )}
                </div>
              ))}

              {/* Valid Dates */}
              <div>
                <label className="text-sm font-medium text-foreground">Valid From</label>
                <input
                  type="datetime-local"
                  name="validFrom"
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card"
                  value={values.validFrom}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.validFrom && errors.validFrom && (
                  <div className="text-red-500 text-sm">{errors.validFrom}</div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground">Valid Till</label>
                <input
                  type="datetime-local"
                  name="validTill"
                  className="w-full px-4 py-2 border border-border rounded-xl bg-card"
                  value={values.validTill}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.validTill && errors.validTill && (
                  <div className="text-red-500 text-sm">{errors.validTill}</div>
                )}
              </div>
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                className="w-full sm:w-[250px] min-h-[50px] flex justify-center items-center text-white bg-primary text-lg font-semibold rounded-xl hover:bg-accent mt-7"
              >
                {loading ? <div className="loader"></div> : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CouponForm;
